import { useTranslation } from 'next-i18next';
import {
  FC, ReactNode, useCallback, useMemo,
} from 'react';
import { useFormState } from 'react-final-form';
import Typography from 'src/common/components/Typography';
import { TripCartFormState } from 'src/listing/types';
import getCaptainOption from 'src/listing/utils/getCaptainOption';
import { useTripCartOptionsContext } from 'src/trip-cart/hooks/useTripCartOptionsContext';
import { useTripCartContext } from 'src/trip-cart/hooks/useTripCartContext';
import { TRIP_CART_PANEL_PRICE_DETAILS } from 'src/listing/constants';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import styles from './TripCartAsidePriceHeader.module.scss';

const placeholder = (value: ReactNode) => value || ' ';

const TripCartAsidePriceHeader: FC = () => {
  const [t] = useTranslation('listing-page');
  const {
    priceLoading,
    prices,
    getFormattedEstimatedRate,
    options: { isCostConfirmed },
    getPricePlaceHolder,
  } = useTripCartOptionsContext();
  const { showPanel } = useTripCartContext();

  const {
    values: {
      withCaptain,
      isFormStateValid,
    },
  } = useFormState<TripCartFormState>();

  const { cost, costText } = useMemo(() => {
    const showEstimatedRate = (!isFormStateValid || prices?.rate != null) && !isCostConfirmed;
    const priceRate = prices?.rate || '';
    let formattedRate = showEstimatedRate ? getFormattedEstimatedRate(priceRate) : '';
    if (!isFormStateValid) {
      formattedRate = getPricePlaceHolder();
    }
    const estimatedRateText = showEstimatedRate ? t('trip-cart--estimated-rate--text') : t('trip-cart--estimated-cost');

    const estimatedCost = prices?.price_sections?.[0]?.formatted_amount || '';

    return {
      cost: showEstimatedRate ? formattedRate : estimatedCost,
      costText: isCostConfirmed ? t('trip-cart--confirmed-cost') : estimatedRateText,
    };
  }, [
    getFormattedEstimatedRate,
    getPricePlaceHolder,
    prices?.price_sections,
    prices?.rate,
    isCostConfirmed,
    t,
    isFormStateValid,
  ]);

  const captainedType = getCaptainOption([withCaptain]);

  const showPriceDetails = useCallback(() => {
    showPanel(TRIP_CART_PANEL_PRICE_DETAILS);
  }, [showPanel]);

  return (
    <div className={styles.root}>
      <div className={styles.left}>
        <SkeletonBlock loading={priceLoading}>
          <Typography
            type="span"
            size="heading-4"
            weight="semibold"
          >
            {placeholder(cost)}
          </Typography>
        </SkeletonBlock>
        <SkeletonBlock loading={priceLoading}>
          <Typography
            type="span"
            size="body-s"
            weight="regular"
          >
            {placeholder(t(`captain-option--${captainedType}`))}
          </Typography>
        </SkeletonBlock>
      </div>
      <SkeletonBlock loading={priceLoading}>
        <button
          className={styles.costType}
          type="button"
          onClick={showPriceDetails}
          disabled={!isFormStateValid}
        >
          <Typography
            type="span"
            size="body-m"
            weight="regular"
          >
            {placeholder(costText)}
          </Typography>
        </button>
      </SkeletonBlock>
    </div>
  );
};

export default TripCartAsidePriceHeader;
